import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAppState } from '../../contexts/AppStateContext';
import FileUpload from '../common/FileUpload';
import ToolHeader from '../common/ToolHeader';
import ProgressBar from '../common/ProgressBar';
import { loadImage, resizeImage } from '../../utils/imageUtils';
import { formatFileSize, downloadFile } from '../../utils/fileUtils';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiDownload, FiLoader, FiInfo } = FiIcons;

const ImageResizeTool = () => {
  const { t } = useLanguage();
  const { state, dispatch } = useAppState();
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');

  useEffect(() => {
    if (state.selectedFile && state.selectedFile.type.startsWith('image/')) {
      loadImagePreview();
    }
  }, [state.selectedFile]);

  useEffect(() => {
    if (state.maintainAspectRatio && state.originalWidth && state.originalHeight) {
      const aspectRatio = state.originalWidth / state.originalHeight;
      const newHeight = Math.round(state.targetWidth / aspectRatio);
      dispatch({
        type: 'UPDATE_SETTINGS',
        payload: { targetHeight: newHeight }
      });
    }
  }, [state.targetWidth, state.maintainAspectRatio]);

  const loadImagePreview = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      setProgress(20);
      setProgressMessage('載入圖片...');

      const img = await loadImage(state.selectedFile);
      const url = URL.createObjectURL(state.selectedFile);

      setProgress(100);
      setProgressMessage('完成');

      dispatch({
        type: 'SET_IMAGE_PREVIEW',
        payload: {
          url,
          width: img.naturalWidth,
          height: img.naturalHeight
        }
      });

      dispatch({ type: 'SET_LOADING', payload: false });
      setProgress(0);
      setProgressMessage('');
    } catch (error) {
      console.error('圖片加載錯誤:', error);
      dispatch({ type: 'SET_ERROR', payload: error.message });
      if (window.showToast) {
        window.showToast(error.message, 'error');
      }
      setProgress(0);
      setProgressMessage('');
    }
  };

  const handleFileUpload = (file) => {
    if (!file.type.startsWith('image/')) {
      if (window.showToast) {
        window.showToast(t('invalidFileType'), 'error');
      }
      return;
    }
    dispatch({ type: 'SET_SELECTED_FILE', payload: file });
  };

  const handleResize = async () => {
    if (!state.selectedFile) {
      if (window.showToast) {
        window.showToast(t('pleaseUploadFile'), 'warning');
      }
      return;
    }

    if (state.targetWidth <= 0 || state.targetHeight <= 0) {
      if (window.showToast) {
        window.showToast('請輸入有效的尺寸', 'warning');
      }
      return;
    }

    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      setProgress(10);
      setProgressMessage('開始調整大小...');

      const img = await loadImage(state.selectedFile);

      setProgress(50);
      setProgressMessage('調整圖片尺寸...');

      const blob = await resizeImage(
        img,
        state.targetWidth,
        state.targetHeight,
        state.imageFormat,
        0.92
      );

      setProgress(90);
      setProgressMessage('生成文件...');

      const url = URL.createObjectURL(blob);

      dispatch({
        type: 'SET_RESIZED_IMAGE',
        payload: {
          blob,
          url,
          size: blob.size
        }
      });

      setProgress(100);
      setProgressMessage('完成');

      if (window.showToast) {
        window.showToast(t('conversionSuccess'), 'success');
      }
    } catch (error) {
      console.error('調整大小錯誤:', error);
      dispatch({ type: 'SET_ERROR', payload: error.message });
      if (window.showToast) {
        window.showToast(error.message, 'error');
      }
    } finally {
      setTimeout(() => {
        setProgress(0);
        setProgressMessage('');
      }, 500);
    }
  };

  const handleDownload = () => {
    if (state.resizedImageBlob) {
      const extension = state.imageFormat === 'image/jpeg' ? 'jpg' :
                       state.imageFormat === 'image/png' ? 'png' : 'webp';
      const originalName = state.selectedFile.name.split('.')[0];
      downloadFile(state.resizedImageBlob, `${originalName}-resized.${extension}`);
    }
  };

  const setPresetSize = (width, height) => {
    dispatch({
      type: 'UPDATE_SETTINGS',
      payload: { targetWidth: width, targetHeight: height }
    });
  };

  const presetSizes = [
    { name: 'HD', width: 1280, height: 720 },
    { name: 'Full HD', width: 1920, height: 1080 },
    { name: 'Instagram', width: 1080, height: 1080 },
    { name: 'Facebook', width: 1200, height: 630 },
    { name: 'Twitter', width: 1200, height: 675 }
  ];

  if (state.showResults && state.resizedImageUrl) {
    const savedSpace = state.originalSize - state.processedSize;
    const ratio = Math.round((savedSpace / state.originalSize) * 100);

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <ToolHeader title={t('resizeResults')} />

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <h4 className="text-sm font-semibold text-gray-600 mb-3">
                {t('originalImage')}
              </h4>
              <img
                src={state.imagePreview}
                alt="Original"
                className="w-full h-64 object-contain bg-gray-100 rounded-lg border"
              />
              <p className="text-sm text-gray-600 mt-2">
                {state.originalWidth} × {state.originalHeight} {t('pixels')} | {formatFileSize(state.originalSize)}
              </p>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-600 mb-3">
                {t('resizedImage')}
              </h4>
              <img
                src={state.resizedImageUrl}
                alt="Resized"
                className="w-full h-64 object-contain bg-gray-100 rounded-lg border"
              />
              <p className="text-sm text-gray-600 mt-2">
                {state.targetWidth} × {state.targetHeight} {t('pixels')} | {formatFileSize(state.processedSize)}
              </p>
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-4 mb-6 border border-green-200">
            <div className="flex items-center gap-2 mb-2">
              <SafeIcon icon={FiInfo} className="text-green-600" />
              <h4 className="font-semibold text-green-800">調整結果</h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white/60 rounded-lg p-3">
                <span className="text-sm text-gray-600">尺寸變化:</span>
                <p className="font-semibold text-green-600">
                  {Math.round((state.targetWidth * state.targetHeight) / (state.originalWidth * state.originalHeight) * 100)}%
                </p>
              </div>
              <div className="bg-white/60 rounded-lg p-3">
                <span className="text-sm text-gray-600">文件大小:</span>
                <p className={`font-semibold ${savedSpace > 0 ? 'text-green-600' : 'text-orange-600'}`}>
                  {savedSpace > 0 ? '-' : '+'}{Math.abs(ratio)}%
                </p>
              </div>
              <div className="bg-white/60 rounded-lg p-3">
                <span className="text-sm text-gray-600">節省空間:</span>
                <p className={`font-semibold ${savedSpace > 0 ? 'text-green-600' : 'text-orange-600'}`}>
                  {savedSpace > 0 ? '-' : '+'}{formatFileSize(Math.abs(savedSpace))}
                </p>
              </div>
            </div>
          </div>

          <div className="flex gap-3 justify-center">
            <motion.button
              className="px-6 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors flex items-center gap-2 shadow-md"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleDownload}
            >
              <SafeIcon icon={FiDownload} />
              {t('downloadImage')}
            </motion.button>

            <motion.button
              className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => dispatch({ type: 'RESET_TOOL' })}
            >
              {t('startOver')}
            </motion.button>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <ToolHeader
        title={t('imageResize')}
        filename={state.selectedFile?.name}
      />

      <div className="p-6">
        <p className="text-gray-600 mb-6">
          {t('imageResizeDesc')}
        </p>

        {!state.showSettings ? (
          <FileUpload
            onFileUpload={handleFileUpload}
            acceptedTypes="image/*"
            uploadText={t('dropOrClickImage')}
            supportText={t('supportImageFormats')}
          />
        ) : (
          <div className="space-y-6">
            {state.imagePreview && (
              <div className="text-center">
                <img
                  src={state.imagePreview}
                  alt="Preview"
                  className="max-w-full max-h-64 mx-auto rounded-lg border"
                />
                <p className="text-sm text-gray-600 mt-2">
                  {t('originalDimensions')}: {state.originalWidth} × {state.originalHeight} {t('pixels')}
                </p>
              </div>
            )}

            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-medium text-gray-800 mb-4">
                {t('sizeSettings')}
              </h4>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    預設尺寸
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                    {presetSizes.map((preset) => (
                      <motion.button
                        key={preset.name}
                        className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm hover:border-purple-400 hover:bg-purple-50 transition-colors"
                        onClick={() => setPresetSize(preset.width, preset.height)}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <div className="font-medium">{preset.name}</div>
                        <div className="text-xs text-gray-500">
                          {preset.width}×{preset.height}
                        </div>
                      </motion.button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('widthPixels')}
                    </label>
                    <input
                      type="number"
                      value={state.targetWidth}
                      onChange={(e) => dispatch({
                        type: 'UPDATE_SETTINGS',
                        payload: { targetWidth: parseInt(e.target.value) || 0 }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      placeholder="800"
                      min="1"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('heightPixels')}
                    </label>
                    <input
                      type="number"
                      value={state.targetHeight}
                      onChange={(e) => dispatch({
                        type: 'UPDATE_SETTINGS',
                        payload: { targetHeight: parseInt(e.target.value) || 0 }
                      })}
                      disabled={state.maintainAspectRatio}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      placeholder="600"
                      min="1"
                    />
                  </div>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="maintainAspect"
                    checked={state.maintainAspectRatio}
                    onChange={(e) => dispatch({
                      type: 'UPDATE_SETTINGS',
                      payload: { maintainAspectRatio: e.target.checked }
                    })}
                    className="mr-2 w-4 h-4 text-purple-600"
                  />
                  <label htmlFor="maintainAspect" className="text-sm text-gray-700">
                    {t('maintainAspectRatio')}
                  </label>
                </div>
              </div>
            </div>

            <AnimatePresence>
              {state.isLoading && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="bg-purple-50 rounded-lg p-4"
                >
                  <ProgressBar
                    progress={progress}
                    message={progressMessage}
                    showPercentage={true}
                  />
                </motion.div>
              )}
            </AnimatePresence>

            <div className="flex gap-3 justify-center">
              <motion.button
                className="px-6 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-md"
                whileHover={{ scale: state.isLoading ? 1 : 1.02 }}
                whileTap={{ scale: state.isLoading ? 1 : 0.98 }}
                onClick={handleResize}
                disabled={state.isLoading}
              >
                {state.isLoading ? (
                  <>
                    <SafeIcon icon={FiLoader} className="animate-spin" />
                    {t('convertingPleaseWait')}
                  </>
                ) : (
                  t('resizeImage')
                )}
              </motion.button>

              <motion.button
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => dispatch({ type: 'RESET_TOOL' })}
              >
                {t('reuploadImage')}
              </motion.button>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default ImageResizeTool;